import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ChevronDown, FileUp, GitBranch, Layers, Pencil, Plus, Sparkles, Trash2, Upload } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { SkillPreset } from "@/api/generated/SkillPreset"
import type { SkillSummary } from "@/api/generated/SkillSummary"
import {
  useDeletePreset,
  useDeleteSkill,
  useSkillPresets,
  useSkills,
  useUpdateSkill,
} from "@/api/queries/skills"
import { useConfirm } from "@/components/app/confirm-dialog"
import { createColumns, DataTable } from "@/components/app/data-table"
import { PageContainer, PageHeader } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { EmptyState, ErrorState } from "@/components/app/states"
import { StatusBadge } from "@/components/app/status-badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { PresetDialog } from "@/features/studio/preset-dialog"
import { GitImportDialog, useMarkdownImport } from "@/features/studio/skill-import"
import { SkillSheet } from "@/features/studio/skill-sheet"
import { formatNumber } from "@/lib/format"

interface SkillsSearch {
  tab?: "presets"
  skill?: number
}

export const Route = createFileRoute("/_app/studio/skills")({
  staticData: { titleKey: "nav.items.skills" },
  validateSearch: (s: Record<string, unknown>): SkillsSearch => ({
    tab: s.tab === "presets" ? "presets" : undefined,
    skill: Number(s.skill) > 0 ? Number(s.skill) : undefined,
  }),
  component: SkillsPage,
})

function SkillsPage() {
  const { t } = useTranslation()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const skills = useSkills()
  const [creating, setCreating] = useState(false)
  const [gitOpen, setGitOpen] = useState(false)
  const [presetDialog, setPresetDialog] = useState<{ open: boolean; preset?: SkillPreset }>({ open: false })
  const setSearch = (patch: Partial<SkillsSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })
  const md = useMarkdownImport((s) => setSearch({ skill: s.id, tab: undefined }))
  const tab = search.tab ?? "skills"

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("studio.skills.title")}
        description={t("studio.skills.description")}
        icon={Sparkles}
        actions={
          tab === "skills" ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant="outline" disabled={md.pending} />}>
                  <Upload />
                  {t("studio.skills.import")}
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-auto min-w-52">
                  <DropdownMenuItem onClick={md.open}>
                    <FileUp />
                    {t("studio.skills.importFile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setGitOpen(true)}>
                    <GitBranch />
                    {t("studio.skills.importGit")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => setCreating(true)} data-testid="new-skill">
                <Plus />
                {t("studio.skills.new")}
              </Button>
            </>
          ) : (
            <Button onClick={() => setPresetDialog({ open: true })} disabled={!skills.data?.length}>
              <Plus />
              {t("studio.skills.presets.new")}
            </Button>
          )
        }
      >
        <Tabs value={tab} onValueChange={(v) => setSearch({ tab: v === "presets" ? "presets" : undefined })}>
          <TabsList variant="line" className="h-8">
            <TabsTrigger value="skills" className="px-2">
              <Sparkles />
              {t("studio.skills.tabs.skills")}
            </TabsTrigger>
            <TabsTrigger value="presets" className="px-2">
              <Layers />
              {t("studio.skills.tabs.presets")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </PageHeader>
      {md.input}
      {tab === "skills" ? (
        <SkillsTable
          onOpen={(id) => setSearch({ skill: id })}
          onCreate={() => setCreating(true)}
          onImport={md.open}
        />
      ) : (
        <PresetsList
          onEdit={(preset) => setPresetDialog({ open: true, preset })}
          onCreate={() => setPresetDialog({ open: true })}
        />
      )}
      <SkillSheet
        open={creating || search.skill != null}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false)
            setSearch({ skill: undefined })
          }
        }}
        skillId={creating ? undefined : search.skill}
      />
      <GitImportDialog open={gitOpen} onOpenChange={setGitOpen} />
      <PresetDialog
        open={presetDialog.open}
        preset={presetDialog.preset}
        skills={skills.data ?? []}
        onOpenChange={(o) => setPresetDialog((d) => ({ ...d, open: o }))}
      />
    </PageContainer>
  )
}

function SkillsTable({
  onOpen,
  onCreate,
  onImport,
}: {
  onOpen: (id: number) => void
  onCreate: () => void
  onImport: () => void
}) {
  const { t } = useTranslation()
  const skills = useSkills()
  const toggle = useUpdateSkill().mutate
  const remove = useDeleteSkill()
  const confirm = useConfirm()

  const columns = useMemo(
    () =>
      createColumns<SkillSummary>((c) => [
        c.accessor("name", {
          header: t("studio.skills.columns.skill"),
          enableHiding: false,
          cell: ({ row }) => (
            <div className="max-w-md min-w-48">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{row.original.name}</span>
                <code className="text-muted-foreground truncate font-mono text-xs">{row.original.slug}</code>
              </div>
              <div className="text-muted-foreground truncate text-xs">
                {row.original.description || t("studio.skills.noDescription")}
              </div>
            </div>
          ),
        }),
        c.accessor("source_type", {
          header: t("studio.skills.columns.source"),
          cell: ({ row }) => {
            const ref = row.original.source_ref as Record<string, string> | null
            return row.original.source_type === "git" ? (
              <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                <GitBranch className="size-3" />
                <span className="max-w-40 truncate">{ref ? `${ref.owner}/${ref.repo}` : "git"}</span>
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">{t("studio.skills.manual")}</span>
            )
          },
        }),
        c.accessor("agent_count", {
          header: t("studio.skills.columns.agents"),
          meta: { align: "end" },
          cell: ({ getValue }) => <span className="tabular-nums">{formatNumber(getValue())}</span>,
        }),
        c.accessor("body_chars", {
          header: t("studio.skills.columns.size"),
          meta: { align: "end" },
          cell: ({ getValue }) => (
            <span className="text-muted-foreground text-xs tabular-nums">
              {t("studio.skills.chars", { n: formatNumber(getValue()) })}
            </span>
          ),
        }),
        c.accessor("updated_at", {
          header: t("studio.skills.columns.updated"),
          sortFn: "datetime",
          cell: ({ getValue }) => <RelativeTime value={getValue()} className="text-muted-foreground" />,
        }),
        c.accessor("enabled", {
          header: t("studio.skills.columns.enabled"),
          meta: { align: "end" },
          cell: ({ row }) => (
            <div
              className="flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Switch
                size="sm"
                checked={row.original.enabled}
                onCheckedChange={(v) => toggle({ id: row.original.id, enabled: v, silent: true })}
                aria-label={t("studio.skills.toggle", { name: row.original.name })}
              />
            </div>
          ),
        }),
      ]),
    [t, toggle],
  )

  return (
    <DataTable
      label={t("studio.skills.title")}
      data={skills.data}
      columns={columns}
      getRowId={(s) => String(s.id)}
      loading={skills.isPending}
      error={skills.error}
      onRetry={() => void skills.refetch()}
      searchable
      searchPlaceholder={t("studio.skills.search")}
      initialSorting={[{ id: "name", desc: false }]}
      onRowClick={(s) => onOpen(s.id)}
      rowActions={(s) => (
        <>
          <DropdownMenuItem onClick={() => onOpen(s.id)}>
            <Pencil />
            {t("common.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={() =>
              void confirm({
                title: t("studio.skills.deleteTitle", { name: s.name }),
                description:
                  s.agent_count > 0
                    ? t("studio.skills.deleteInUse", { count: s.agent_count })
                    : t("studio.skills.deleteDescription"),
                confirmLabel: t("studio.skills.delete"),
                destructive: true,
                onConfirm: () => remove.mutateAsync(s),
              })
            }
          >
            <Trash2 />
            {t("studio.skills.delete")}
          </DropdownMenuItem>
        </>
      )}
      emptyState={
        <EmptyState
          variant="plain"
          icon={Sparkles}
          title={t("studio.skills.empty.title")}
          description={t("studio.skills.empty.description")}
          action={
            <Button size="sm" onClick={onCreate}>
              <Plus />
              {t("studio.skills.new")}
            </Button>
          }
          secondaryAction={
            <Button size="sm" variant="outline" onClick={onImport}>
              <FileUp />
              {t("studio.skills.importFile")}
            </Button>
          }
        />
      }
    />
  )
}

function PresetsList({ onEdit, onCreate }: { onEdit: (p: SkillPreset) => void; onCreate: () => void }) {
  const { t } = useTranslation()
  const presets = useSkillPresets()
  const skills = useSkills()
  const remove = useDeletePreset()
  const confirm = useConfirm()
  const nameOf = (id: number) => skills.data?.find((s) => s.id === id)?.name

  if (presets.isPending)
    return (
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    )
  if (presets.isError) return <ErrorState error={presets.error} onRetry={() => void presets.refetch()} />
  if (presets.data.length === 0)
    return (
      <EmptyState
        icon={Layers}
        title={t("studio.skills.presets.empty.title")}
        description={t("studio.skills.presets.empty.description")}
        action={
          <Button size="sm" onClick={onCreate} disabled={!skills.data?.length}>
            <Plus />
            {t("studio.skills.presets.new")}
          </Button>
        }
      />
    )
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {presets.data.map((p) => (
        <div key={p.id} className="bg-card group flex flex-col gap-3 rounded-xl border p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate font-medium">{p.name}</h3>
              <p className="text-muted-foreground line-clamp-2 text-xs">
                {p.description || t("studio.skills.noDescription")}
              </p>
            </div>
            <div className="flex shrink-0 gap-0.5">
              <Button variant="ghost" size="icon-sm" onClick={() => onEdit(p)} aria-label={t("common.edit")}>
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                className="hover:text-destructive"
                aria-label={t("studio.skills.presets.delete")}
                onClick={() =>
                  void confirm({
                    title: t("studio.skills.presets.deleteTitle", { name: p.name }),
                    description: t("studio.skills.presets.deleteDescription"),
                    confirmLabel: t("studio.skills.presets.delete"),
                    destructive: true,
                    onConfirm: () => remove.mutateAsync(p),
                  })
                }
              >
                <Trash2 />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {p.skill_ids.length === 0 ? (
              <span className="text-muted-foreground text-xs">{t("studio.skills.presets.noSkills")}</span>
            ) : (
              p.skill_ids.map((id) => <StatusBadge key={id}>{nameOf(id) ?? `#${id}`}</StatusBadge>)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
